import { getPaletteData } from '../data/getPaletteData';
import { getPageData } from '../data/getPageData';
import { analyticsEventHandler } from './analyticsEventHandler';

const CLIENT_API_EVENT_TO_EVENT_HANDLER_MAP = {
  getPaletteData,
  getPageData,
  analytics: analyticsEventHandler,
};

/**
 * Checks if the given index represents the start of a new chunk.
 * A chunk is defined by its size, and an index that is a multiple of
 * the chunk size indicates the beginning of a new chunk.
 *
 * @param index - The index to check.
 * @param chunkSize - The size of chunks to divide into.
 * @returns A boolean indicating whether the given index starts a new chunk.
 */
const isNewChunk = (index: number, chunkSize: number) =>
  index % chunkSize === 0;

/**
 * Divides a list of items into smaller chunks of a specified size.
 *
 * The function iterates over the given list of items and splits them into sub-arrays
 * (chunks) each with a length defined by the `chunkSize`. It returns an array of these chunks.
 *
 * @param items - The array of items to be divided into chunks.
 * @param chunkSize - The size of each chunk.
 * @returns An array of item chunks, where each chunk is a sub-array of the original items.
 */
const getSubscribedEventsInChunks = (items: any[], chunkSize: number) => {
  let currentChunk = [];
  return items.reduce((chunks, item, index) => {
    if (isNewChunk(index, chunkSize)) {
      currentChunk = [item];
      chunks.push(currentChunk);
    } else {
      currentChunk.push(item);
    }
    return chunks;
  }, []);
};

/**
 * Handles an event by storing the event name and its associated parameters.
 *
 * This function checks if there is a global subscription map `window.__mmClientApiSubscriptions__`.
 * If it does not exist, it initializes it as a new Map. Then, it verifies if there is an existing
 * subscription list for the given event name. If not, it initializes an empty array for that event
 * name in the subscription map. Finally, it appends the provided parameters to the list of
 * subscriptions for the specified event name.
 *
 * @param eventName - The name of the event to handle.
 * @param params - The parameters associated with the event.
 */
const handleEvent = (eventName: string, params: any) => {
  if (!window.__mmClientApiSubscriptions__) {
    window.__mmClientApiSubscriptions__ = new Map();
  }
  if (!window.__mmClientApiSubscriptions__.get(eventName)) {
    window.__mmClientApiSubscriptions__.set(eventName, []);
  }
  window.__mmClientApiSubscriptions__.get(eventName).push(params);
};

/**
 * Initializes the client API by setting up event handlers and managing event subscriptions.
 *
 * This function prepares the client API for handling events by ensuring the `mmClientApi`
 * object is properly initialized and capable of storing event subscriptions.
 *
 * It checks if the `mmClientApi` was utilized before its proper initialization.
 * If so, it processes existing events and reinitializes the `mmClientApi` correctly.
 *
 * The function defines the `push` method for the `mmClientApi` object, which is responsible
 * for adding new events and their parameters to the subscription list.
 */
const innerInitClientApi = () => {
  const isMMClientApiUsedBeforeInitialization = Array.isArray(
    window.mmClientApi
  );
  const isMMClientApiInitialized =
    window.mmClientApi &&
    !Array.isArray(window.mmClientApi) &&
    window.mmClientApi.push;
  const initialization = () => {
    window.mmClientApi = {
      push: (eventName: string, params: any) => handleEvent(eventName, params),
    };
  };
  if (isMMClientApiUsedBeforeInitialization) {
    getSubscribedEventsInChunks(window.mmClientApi, 2).forEach(
      ([eventName, params]: [string, any]) => handleEvent(eventName, params)
    );
    initialization();
  } else if (!isMMClientApiInitialized) {
    initialization();
  }
};

/**
 * Sets the event emitter for a given event name and handler.
 *
 * This function initializes the client API if it hasn't been initialized yet.
 * It then checks if there are already subscriptions for the given event name.
 * If there are, it invokes the event handler for each of the existing subscriptions.
 * Finally, it sets the event emitter for the given event name by adding a method to
 * handle new parameters for the event.
 *
 * @param eventName - The name of the event for which to set the emitter.
 * @param eventHandler - The handler function to be called with event parameters.
 */
export const setEventEmitter = (eventName: string, eventHandler: any) => {
  if (
    window.__mmClientApiSubscriptions__ &&
    window.__mmClientApiSubscriptions__.get(eventName) &&
    window.__mmClientApiSubscriptions__.get(eventName).length
  ) {
    window.__mmClientApiSubscriptions__
      .get(eventName)
      .forEach((params: any) => eventHandler(params));
  }
  if (window.__mmClientApiSubscriptions__) {
    window.__mmClientApiSubscriptions__.set(eventName, {
      push: (params: any) => eventHandler(params),
    });
  } else {
    window.__mmClientApiSubscriptions__ = new Map();
    window.__mmClientApiSubscriptions__.set(eventName, {
      push: (params: any) => eventHandler(params),
    });
  }
};

/**
 * Initializes the client API event handlers by setting up the event emitters for each
 * event-handler pair defined in CLIENT_API_EVENT_TO_EVENT_HANDLER_MAP.
 * This function iterates over the entries in CLIENT_API_EVENT_TO_EVENT_HANDLER_MAP,
 * which maps event names to their respective handler functions, and calls setEventEmitter
 * to initialize each event emitter with the corresponding event handler.
 */
export const initClientApiEvents = () => {
  innerInitClientApi();

  Object.entries(CLIENT_API_EVENT_TO_EVENT_HANDLER_MAP).forEach(
    ([eventName, eventHandler]) => {
      setEventEmitter(eventName, eventHandler);
    }
  );
};
